import { apiImageAnalysis } from 'apis/SensorApi';
import { getUrl, list } from 'aws-amplify/storage';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { useDialog } from 'context/DialogContext';
import { useSensors } from 'context/SensorsContext';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import './CameraDisplayer.css';

const NOT_FOUND_VIDEO_FILE = 'not_found';
export default function CameraDisplayer(props) {
  const [image1Src, setImage1Src] = useState('')
  const [image2Src, setImage2Src] = useState('')
  const [videoFileName, setVideoFileName] = useState(undefined)

  const {
    sensorId,
    isDisplayVideo,
    isImageAnalysis,
    isSwap,
    isDouble,
    isInList,
    imageProps,
    buttonStatusProps,
  } = props

  useEffect(() => {
    fetchVideoData()
  }, [sensorId])

  useEffect(() => {
    const fetchImageData = async () => {
      if (imageProps.key1) {
        const url = await getUrl({ key: imageProps.key1 })
        setImage1Src(url.url.toString())
      }
      if (imageProps.key2) {
        const url = await getUrl({ key: imageProps.key2 })
        setImage2Src(url.url.toString())
      }
    }

    fetchImageData();
  }, [imageProps.key1, imageProps.key2])

  useEffect(() => {
    if (imageProps.src1) {
      setImage1Src(imageProps.src1)
    }
    if (imageProps.src2) {
      setImage2Src(imageProps.src2)
    }
  }, [imageProps.src1, imageProps.src2])

  const listVideoFiles = async () => {
    const response = await list({
      prefix: `camera-videos/${sensorId}_1`,
      listAll: true,
    });
    return response.items ? response.items.map(item => item.key.split('/')[2]) : [];
  };

  const fetchVideoData = async () => {
    const listVideoFileName = await listVideoFiles();
    if (listVideoFileName.length === 0) return;
    setVideoFileName((prev) => {
      const index = listVideoFileName.indexOf(prev)
      if (index === -1) {
        return listVideoFileName.length > 1 ? listVideoFileName[listVideoFileName.length - 2] : listVideoFileName[0];
      }
      return listVideoFileName[index + 1] ?? NOT_FOUND_VIDEO_FILE;
    })
  };

  const handleVideoEnded = () => {
    fetchVideoData()
  }

  const handleVideoError = async () => {
    const listVideoFileName = await listVideoFiles();
    if (listVideoFileName.length === 0) return;
    setVideoFileName(prev => {
      return prev === NOT_FOUND_VIDEO_FILE ? NOT_FOUND_VIDEO_FILE : listVideoFileName[listVideoFileName.length - 2];
    })
  }

  const generateItemProps = (imageSrc, labelSuffix, videoKeySuffix, cameraNumber) => ({
    imageProps: {
      src: imageSrc,
      onError: () => {
        if (cameraNumber === 1) {
          setImage1Src(undefined)
        } else {
          setImage2Src(undefined)
        }
      },
    },
    videoProps: {
      videoKey: `camera-videos/${sensorId}_${cameraNumber}/${videoKeySuffix}`,
      onVideoEnded: cameraNumber === 1 ? handleVideoEnded : () => null,
      onVideoError: cameraNumber === 1 ? handleVideoError : () => null,
    },
    buttonStatusProps: {
      sensorId: sensorId,
      cameraNumber: cameraNumber,
      status: buttonStatusProps?.status[`camera${cameraNumber}`],
      toolTipContent: buttonStatusProps?.toolTipContent[`camera${cameraNumber}`],
    },
    isDisplayVideo: isDisplayVideo,
    isImageAnalysis: isImageAnalysis,
    isDouble: isDouble,
    isInList: isInList,
    label: props.label && props.label + labelSuffix,
    popupText: props.popupText,
    style: props.style,
    onLoad: props.onLoad,
    onClick: () => {
      props.onClick({ id: sensorId }, { cameraNumber: cameraNumber })
    },
    subComponent: (size) => props.subComponent && props.subComponent({ src: imageSrc, cameraNumber, size }),
  });

  return isDouble ? (
    <>
      <Item {...generateItemProps(isSwap ? image2Src : image1Src, '_L', videoFileName, isSwap ? 2 : 1)} />
      <Item {...generateItemProps(isSwap ? image1Src : image2Src, '_R', videoFileName, isSwap ? 1 : 2)} />
    </>
  ) : (
    <Item {...generateItemProps(image1Src, '', videoFileName, 1)} />
  )
}

CameraDisplayer.propTypes = {
  sensorId: PropTypes.string.isRequired,
  isDisplayVideo: PropTypes.bool,
  isImageAnalysis: PropTypes.bool,
  isSwap: PropTypes.bool,
  isDouble: PropTypes.bool,
  isInList: PropTypes.bool,
  label: PropTypes.string,
  popupText: PropTypes.string,
  style: PropTypes.object,
  onLoad: PropTypes.func,
  onClick: PropTypes.func,
  subComponent: PropTypes.func,
  imageProps: PropTypes.shape({
    src1: PropTypes.string,
    src2: PropTypes.string,
    key1: PropTypes.string,
    key2: PropTypes.string,
    style: PropTypes.object,
  }),
  buttonStatusProps: PropTypes.shape({
    status: PropTypes.shape({
      camera1: PropTypes.shape({
        color: PropTypes.string,
        value: PropTypes.string,
      }),
      camera2: PropTypes.shape({
        color: PropTypes.string,
        value: PropTypes.string,
      }),
    }),
    toolTipContent: PropTypes.shape({
      camera1: PropTypes.string,
      camera2: PropTypes.string,
    })
  })
}

const Item = (props) => {
  const {
    imageProps,
    videoProps,
    buttonStatusProps,
    isDisplayVideo,
    isImageAnalysis,
    isDouble,
    isInList,
    label,
    style,
    popupText,
    subComponent,
    onClick
  } = props
  const [size, setSize] = useState({ width: 0, height: 0 })

  const handleLoad = (e) => {
    setSize({ width: e.target.offsetWidth, height: e.target.offsetHeight })
  }

  return <div className='camera_diplayer_item_container' style={style}>
    {isImageAnalysis && <ButtonStatus {...buttonStatusProps} style={{ top: isInList || isDouble ? 20 : -100 }} />}
    <div className='camera_diplayer_item_inner' onClick={onClick}>
      <div className='camera_diplayer_item_overlay' />
      {popupText && <div className='camera_diplayer_item_popup'>{popupText}</div>}
      {isDisplayVideo ?
        <Video {...videoProps} label={label} onLoad={handleLoad} onClick={onClick} /> :
        <Image {...imageProps} label={label} onLoad={handleLoad} />}
      {label && <div className='camera_diplayer_item_label'>{label}</div>}
    </div>
    {subComponent && subComponent(size)}
  </div >
}

Item.propTypes = {
  imageProps: PropTypes.shape({
    src: PropTypes.string,
    style: PropTypes.object,
    onError: PropTypes.func,
  }),
  videoProps: PropTypes.shape({
    videoKey: PropTypes.string,
    onVideoEnded: PropTypes.func,
  }),
  buttonStatusProps: PropTypes.shape({
    sensorId: PropTypes.string,
    cameraNumber: PropTypes.number,
    status: PropTypes.shape({
      color: PropTypes.string,
      value: PropTypes.string,
    }),
    toolTipContent: PropTypes.string,
  }),
  style: PropTypes.object,
  isDisplayVideo: PropTypes.bool,
  isImageAnalysis: PropTypes.bool,
  isDouble: PropTypes.bool,
  isInList: PropTypes.bool,
  label: PropTypes.string,
  popupText: PropTypes.string,
  subComponent: PropTypes.func,
  onLoad: PropTypes.func,
  onClick: PropTypes.func,
}



const Image = (props) => {
  return (
    !props.src ?
      <div className='camera_diplayer_item_content_fallback' /> :
      <img
        className='camera_diplayer_item_content'
        src={props.src}
        alt=' '
        onLoad={props.onLoad}
        onError={props.onError}
      />
  )
}

Image.propTypes = {
  src: PropTypes.string,
  key: PropTypes.string,
  style: PropTypes.object,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
}

const Video = (props) => {
  const [src, setSrc] = useState()

  useEffect(() => {
    const fetchVideoUrl = async () => {
      const url = await getUrl({ key: props.videoKey })
      setSrc(url.url.toString())
    }
    fetchVideoUrl()
  }, [props.videoKey])

  return (
    <video
      muted
      className='camera_diplayer_item_content'
      src={src}
      controls={true}
      loop={false}
      autoPlay
      onEnded={props.onVideoEnded}
      onError={props.onVideoError}
      playsInline={1}
      webkitplaysinline={1}
      onLoadedMetadata={(e) => {
        props.onLoad && props.onLoad(e)
      }}
      onClick={(e) => {
        e.stopPropagation()
        props.onClick && props.onClick()
      }}
    />
  );
}

Video.propTypes = {
  videoKey: PropTypes.string,
  onVideoEnded: PropTypes.func,
  onVideoError: PropTypes.func,
  onLoad: PropTypes.func,
  onClick: PropTypes.func,
}

const ButtonStatus = (props) => {
  const [isLoading, setIsLoading] = useState(false)
  const { error } = useDialog()
  const { reload } = useSensors()

  const { sensorId, cameraNumber, status, toolTipContent } = props

  const handleClick = async () => {
    try {
      if (isLoading) return
      setIsLoading(true)
      const res = await apiImageAnalysis({ sensorId, cameraNumber })
      if (!res.success) {
        error('画像解析に失敗しました。')
        setIsLoading(false)
        return
      }
      reload()
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  return (
    <div className='camera_diplayer_item_button_status_wrapper' style={{ ...props.style }}>
      {!isLoading && toolTipContent && <div className='camera_diplayer_item_button_status_tooltip_wrapper'>
        <div className='camera_diplayer_item_button_status_tooltip_content'>{toolTipContent}</div>
      </div>}
      <button
        className='camera_diplayer_item_button_status'
        onClick={handleClick}
        style={{ backgroundColor: status.color, opacity: isLoading ? 0.6 : 1 }}
      >
        {isLoading ? <FanLoadingIcon strokeWidth={2} size={20} color='#fff' /> : status.value}
      </button>
    </div>
  )
}

ButtonStatus.propTypes = {
  sensorId: PropTypes.string.isRequired,
  cameraNumber: PropTypes.number,
  status: PropTypes.shape({
    color: PropTypes.string,
    value: PropTypes.string,
  }),
  toolTipContent: PropTypes.string,
  style: PropTypes.object,
}
